import {
  AssetPreview,
  assetDetails,
  assetName,
} from "@/components/assets/asset-presenter";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/shell/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  browserAssetExplorerStorage,
  readAssetExplorerView,
  readAssetTypeFilter,
  writeAssetExplorerView,
  writeAssetTypeFilter,
  type AssetExplorerView,
  type AssetTypeFilter,
} from "@/lib/asset-explorer-preferences";
import {
  ASSET_DRAG_MIME,
  FOLDER_DRAG_MIME,
  buildFolderTree,
  filterAssets,
  isFolderDropAllowed,
  nextSelection,
  parseAssetDrag,
  parseFolderDrag,
  type AssetSmartView,
  type AssetSort,
  type FolderNode,
} from "@/lib/asset-library";
import { cn } from "@/lib/cn";
import {
  MAX_ASSET_BYTES,
  useAssetFolders,
  useAssetRevisions,
  useAssetUsage,
  useAssets,
  useBulkAssets,
  useContextTree,
  useCreateAssetFolder,
  useDeleteAssetFolder,
  usePurgeAsset,
  useReplaceAsset,
  useRestoreAsset,
  useTrashAsset,
  useUpdateAsset,
  useUpdateAssetFolder,
  useUploadAsset,
  type Asset,
  type AssetBulkOperation,
  type AssetFolder,
  type AssetStatus,
} from "@/lib/queries";
import { useContextStore } from "@/stores/context";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Accessibility,
  ArrowDownAZ,
  CalendarDays,
  Check,
  Clock3,
  ArchiveRestore,
  Download,
  FilePenLine,
  FileType2,
  Folder,
  FolderInput,
  FolderPlus,
  Gauge,
  Grid2X2,
  Images,
  Library,
  List,
  MoreHorizontal,
  Pencil,
  Search,
  Tag,
  Tags,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authed/assets")({
  component: AssetsPage,
});
export { assetName } from "@/components/assets/asset-presenter";
const ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.woff2,.ttf,.otf";

async function downloadAsset(asset: Asset): Promise<void> {
  const response = await fetch(asset.url);
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = assetName(asset);
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

interface QueueItem {
  id: string;
  file: File;
  folderId: string | null;
  state: "pending" | "uploading" | "success" | "error";
  error?: string;
}

export function AssetsPage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const orgId = useContextStore((s) => s.orgId);
  const { data: orgs } = useContextTree();
  const canWrite = orgs?.find((org) => org.id === orgId)?.role !== "viewer";
  const [status, setStatus] = useState<AssetStatus>("active");
  const assetsQuery = useAssets(appId, status);
  const foldersQuery = useAssetFolders(appId);
  const upload = useUploadAsset(appId);
  const bulk = useBulkAssets(appId);
  const createFolder = useCreateAssetFolder(appId);
  const quickUpdateAsset = useUpdateAsset(appId);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [smart, setSmart] = useState<AssetSmartView>(null);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState<string | null>(null);
  const [days, setDays] = useState<0 | 7 | 30 | 90>(0);
  const [sort, setSort] = useState<AssetSort>("newest");
  const [type, setTypeState] = useState<AssetTypeFilter>(() =>
    readAssetTypeFilter(browserAssetExplorerStorage())
  );
  const [view, setViewState] = useState<AssetExplorerView>(() =>
    readAssetExplorerView(browserAssetExplorerStorage())
  );
  const [selection, setSelection] = useState(new Set<string>());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [navigatorOpen, setNavigatorOpen] = useState(false);
  const [renameAsset, setRenameAsset] = useState<Asset | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const running = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data]);
  const assets = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data]);
  const visible = useMemo(
    () =>
      filterAssets(assets, { query, type, tag, days, sort, folderId, smart }),
    [assets, days, folderId, query, smart, sort, tag, type]
  );
  const tags = useMemo(
    () => [...new Set(assets.flatMap((asset) => asset.tags))].sort(),
    [assets]
  );
  const selectedAsset = assets.find((asset) => asset.id === detailId) ?? null;
  const scopeLabel =
    status === "trash"
      ? "Trash"
      : folderId
      ? folders.find((folder) => folder.id === folderId)?.name ?? "Folder"
      : smart
      ? smart.replace(/-/g, " ")
      : "All assets";

  useEffect(() => {
    // Scope changes are navigation events: file-manager selection must never leak across them.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelection(new Set());
    setAnchor(null);
    setDetailId(null);
  }, [appId, folderId, smart, status]);
  useEffect(() => {
    const visibleIds = new Set(visible.map((asset) => asset.id));
    // Filters define the actionable selection. Hidden assets must never remain selected for bulk
    // work after search/type/tag/date changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelection((current) => {
      const next = new Set([...current].filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
    setAnchor((current) =>
      current && visibleIds.has(current) ? current : null
    );
  }, [visible]);
  useEffect(() => {
    const pending = queue
      .filter((item) => item.state === "pending")
      .slice(0, Math.max(0, 3 - running.current));
    for (const item of pending) {
      running.current += 1;
      setQueue((items) =>
        items.map((entry) =>
          entry.id === item.id ? { ...entry, state: "uploading" } : entry
        )
      );
      upload
        .mutateAsync({ file: item.file, folderId: item.folderId })
        .then(() => {
          setQueue((items) =>
            items.map((entry) =>
              entry.id === item.id ? { ...entry, state: "success" } : entry
            )
          );
        })
        .catch((error: Error) => {
          setQueue((items) =>
            items.map((entry) =>
              entry.id === item.id
                ? { ...entry, state: "error", error: error.message }
                : entry
            )
          );
        })
        .finally(() => {
          running.current -= 1;
        });
    }
  }, [queue, upload]);

  function setView(value: AssetExplorerView) {
    setViewState(value);
    writeAssetExplorerView(browserAssetExplorerStorage(), value);
  }
  function setType(value: AssetTypeFilter) {
    setTypeState(value);
    writeAssetTypeFilter(browserAssetExplorerStorage(), value);
  }
  function enqueue(files: FileList | null) {
    const next = Array.from(files ?? []).map((file) =>
      file.size > MAX_ASSET_BYTES
        ? {
            id: crypto.randomUUID(),
            file,
            folderId,
            state: "error" as const,
            error: `${Math.round(file.size / 1024)} KB — limit 500 KB`,
          }
        : { id: crypto.randomUUID(), file, folderId, state: "pending" as const }
    );
    setQueue((items) => [...items, ...next]);
  }
  async function runBulk(operation: AssetBulkOperation) {
    if (!canWrite) return;
    const chunks = Array.from(selection);
    let completed = 0;
    try {
      for (let index = 0; index < chunks.length; index += 100) {
        await bulk.mutateAsync({
          assetIds: chunks.slice(index, index + 100),
          operation,
        });
        completed += Math.min(100, chunks.length - index);
      }
      toast.success(`Updated ${completed} asset${completed === 1 ? "" : "s"}.`);
      setSelection(new Set());
    } catch (error) {
      toast.error(
        `Updated ${completed} of ${chunks.length}. ${
          error instanceof Error
            ? error.message
            : "The remaining assets were not changed."
        }`
      );
    }
  }
  function select(asset: Asset, event: React.MouseEvent) {
    const result = nextSelection(
      visible.map((item) => item.id),
      selection,
      asset.id,
      anchor,
      { toggle: event.metaKey || event.ctrlKey, range: event.shiftKey }
    );
    setSelection(result.selected);
    setAnchor(result.anchor);
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Assets"
        description="Organize, inspect, and safely replace the images and bundled fonts used by your experiences."
        actions={
          canWrite ? (
            <Button onClick={() => input.current?.click()} disabled={!appId}>
              <Upload className="size-4" />
              Upload assets
            </Button>
          ) : undefined
        }
      />
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          enqueue(event.target.files);
          event.target.value = "";
        }}
      />
      <div className="flex min-w-0 overflow-hidden rounded-2xl border border-border/70 bg-background shadow-sm">
        <aside className="hidden w-60 shrink-0 border-r bg-muted/20 p-3 lg:block">
          <Navigator
            appId={appId}
            folders={folders}
            folderId={folderId}
            smart={smart}
            status={status}
            onScope={(nextStatus, nextFolder, nextSmart) => {
              setStatus(nextStatus);
              setFolderId(nextFolder);
              setSmart(nextSmart);
            }}
            canWrite={canWrite}
            onCreate={(name, parentId) =>
              createFolder.mutate(
                { name, parent_id: parentId },
                { onError: (error: Error) => toast.error(error.message) }
              )
            }
          />
        </aside>
        <main className="min-w-0 flex-1">
          <div className="space-y-3 border-b bg-background/95 p-3.5 backdrop-blur">
            <div className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/30 p-1.5">
              <Button
                variant="outline"
                className="h-9 border-border/70 bg-background shadow-none lg:hidden"
                onClick={() => setNavigatorOpen(true)}
              >
                <Library className="size-4" />
                {scopeLabel}
              </Button>
              <div className="relative min-w-52 flex-1">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search assets"
                  className="h-9 border-border/70 bg-background pl-9 shadow-none"
                />
              </div>
              <ModernSelect
                label="Type"
                icon={<FileType2 />}
                value={type}
                onChange={(value) => setType(value as AssetTypeFilter)}
                options={[
                  ["all", "All types"],
                  ["images", "Images"],
                  ["fonts", "Fonts"],
                ]}
              />
              <ModernSelect
                label="Tag"
                icon={<Tag />}
                value={tag ?? "__any"}
                onChange={(value) => setTag(value === "__any" ? null : value)}
                options={[
                  ["__any", "Any tag"],
                  ...tags.map((value): [string, string] => [value, value]),
                ]}
              />
              <ModernSelect
                label="Date"
                icon={<CalendarDays />}
                value={String(days)}
                onChange={(value) => setDays(Number(value) as 0 | 7 | 30 | 90)}
                options={[
                  ["0", "Any date"],
                  ["7", "7 days"],
                  ["30", "30 days"],
                  ["90", "90 days"],
                ]}
              />
              <ModernSelect
                label="Sort"
                icon={<ArrowDownAZ />}
                value={sort}
                onChange={(value) => setSort(value as AssetSort)}
                options={[
                  ["newest", "Newest"],
                  ["oldest", "Oldest"],
                  ["name-asc", "Name A–Z"],
                  ["name-desc", "Name Z–A"],
                  ["largest", "Largest"],
                  ["smallest", "Smallest"],
                ]}
              />
            </div>
            <div className="flex items-center justify-between px-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="font-medium text-foreground">
                  {scopeLabel}
                </span>
                <span>·</span>
                <span>
                  {visible.length} asset{visible.length === 1 ? "" : "s"}
                </span>
              </span>
              <span className="flex rounded-lg border bg-muted/20 p-0.5">
                <Button
                  size="icon"
                  variant={view === "grid" ? "secondary" : "ghost"}
                  className="size-7"
                  aria-label="Grid view"
                  onClick={() => setView("grid")}
                >
                  <Grid2X2 className="size-4" />
                </Button>
                <Button
                  size="icon"
                  variant={view === "list" ? "secondary" : "ghost"}
                  className="size-7"
                  aria-label="List view"
                  onClick={() => setView("list")}
                >
                  <List className="size-4" />
                </Button>
              </span>
            </div>
          </div>
          {canWrite && selection.size ? (
            <BulkToolbar
              count={selection.size}
              folders={folders}
              status={status}
              pending={bulk.isPending}
              onRun={(operation) => void runBulk(operation)}
              onClear={() => setSelection(new Set())}
            />
          ) : null}
          <div className="min-h-96 p-4">
            {assetsQuery.isLoading ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
                {[0, 1, 2, 3].map((item) => (
                  <Skeleton key={item} className="h-40" />
                ))}
              </div>
            ) : assetsQuery.isError ? (
              <EmptyState
                title="Assets could not be loaded"
                description={
                  assetsQuery.error instanceof Error
                    ? assetsQuery.error.message
                    : "Try again."
                }
                action={
                  <Button onClick={() => void assetsQuery.refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : visible.length === 0 ? (
              <EmptyState
                title={
                  assets.length
                    ? "No matching assets"
                    : status === "trash"
                    ? "Trash is empty"
                    : "No assets yet"
                }
                description={
                  assets.length
                    ? "Change the folder, smart view, or filters."
                    : status === "trash"
                    ? "Trashed assets appear here until permanently purged."
                    : "Upload an image or bundled font to get started."
                }
                action={
                  canWrite && status === "active" ? (
                    <Button onClick={() => input.current?.click()}>
                      Upload assets
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <AssetGrid
                appId={appId}
                canWrite={canWrite}
                assets={visible}
                view={view}
                selected={selection}
                onSelect={select}
                onOpen={(asset) => setDetailId(asset.id)}
                onRename={setRenameAsset}
                onDownload={(asset) =>
                  void downloadAsset(asset).catch((error: Error) =>
                    toast.error(error.message)
                  )
                }
                onSelectAll={() =>
                  setSelection(new Set(visible.map((asset) => asset.id)))
                }
              />
            )}
          </div>
        </main>
      </div>
      <UploadQueue
        items={queue}
        onRetry={(id) =>
          setQueue((items) =>
            items.map((item) => {
              if (item.id !== id) return item;
              const { error: _error, ...rest } = item;
              return { ...rest, state: "pending" };
            })
          )
        }
        onDismiss={(id) =>
          setQueue((items) => items.filter((item) => item.id !== id))
        }
      />
      <Dialog open={navigatorOpen} onOpenChange={setNavigatorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Library</DialogTitle>
            <DialogDescription>
              Choose a folder, smart view, or Trash.
            </DialogDescription>
          </DialogHeader>
          <Navigator
            appId={appId}
            folders={folders}
            folderId={folderId}
            smart={smart}
            status={status}
            onScope={(s, f, v) => {
              setStatus(s);
              setFolderId(f);
              setSmart(v);
              setNavigatorOpen(false);
            }}
            canWrite={canWrite}
            onCreate={(name, parentId) =>
              createFolder.mutate(
                { name, parent_id: parentId },
                { onError: (error: Error) => toast.error(error.message) }
              )
            }
          />
        </DialogContent>
      </Dialog>
      <AssetDetail
        key={selectedAsset?.id ?? "none"}
        asset={selectedAsset}
        folders={folders}
        canWrite={canWrite}
        onClose={() => setDetailId(null)}
      />
      <RenameAssetDialog
        key={renameAsset?.id ?? "rename-none"}
        asset={renameAsset}
        pending={quickUpdateAsset.isPending}
        onClose={() => setRenameAsset(null)}
        onSave={(displayName) => {
          if (!renameAsset) return;
          quickUpdateAsset.mutate(
            { id: renameAsset.id, display_name: displayName },
            {
              onSuccess: () => {
                toast.success("File renamed");
                setRenameAsset(null);
              },
              onError: (error: Error) => toast.error(error.message),
            }
          );
        }}
      />
    </div>
  );
}

function ModernSelect({
  label,
  icon,
  value,
  onChange,
  options,
}: {
  label: string;
  icon: React.ReactElement;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-label={label}
        className="h-9 w-auto min-w-32 gap-2 border-border/70 bg-background shadow-none"
      >
        <span className="text-muted-foreground [&>svg]:size-3.5">{icon}</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(([key, text]) => (
          <SelectItem key={key} value={key}>
            {text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Navigator({
  appId,
  folders,
  folderId,
  smart,
  status,
  onScope,
  canWrite,
  onCreate,
}: {
  appId: string | null;
  folders: AssetFolder[];
  folderId: string | null;
  smart: AssetSmartView;
  status: AssetStatus;
  onScope: (
    status: AssetStatus,
    folder: string | null,
    smart: AssetSmartView
  ) => void;
  canWrite: boolean;
  onCreate: (name: string, parent: string | null) => void;
}) {
  const moveAssets = useBulkAssets(appId);
  const updateFolder = useUpdateAssetFolder(appId);
  const deleteFolder = useDeleteAssetFolder(appId);
  const [folderEditor, setFolderEditor] = useState<{
    mode: "create" | "rename";
    parentId: string | null;
    folder?: AssetFolder;
  } | null>(null);
  const [folderName, setFolderName] = useState("");
  const [pendingDelete, setPendingDelete] = useState<AssetFolder | null>(null);
  const tree = buildFolderTree(folders);
  const smartViews: [AssetSmartView, string, React.ReactElement][] = [
    ["untagged", "Untagged", <Tags key="untagged" />],
    [
      "missing-alt",
      "Missing accessibility",
      <Accessibility key="missing-alt" />,
    ],
    ["missing-title", "Missing title", <FilePenLine key="missing-title" />],
    ["near-limit", "Near 500 KB limit", <Gauge key="near-limit" />],
    [
      "recently-replaced",
      "Recently replaced",
      <Clock3 key="recently-replaced" />,
    ],
  ];
  function openFolderEditor(
    mode: "create" | "rename",
    parentId: string | null,
    folder?: AssetFolder
  ): void {
    setFolderName(folder?.name ?? "");
    setFolderEditor({ mode, parentId, ...(folder ? { folder } : {}) });
  }
  function saveFolder(): void {
    const name = folderName.trim();
    if (!name || !folderEditor) return;
    if (folderEditor.mode === "create") onCreate(name, folderEditor.parentId);
    else if (folderEditor.folder)
      updateFolder.mutate(
        { id: folderEditor.folder.id, name },
        { onError: (error: Error) => toast.error(error.message) }
      );
    setFolderEditor(null);
  }
  return (
    <>
      <nav aria-label="Asset library" className="space-y-5">
        <div className="space-y-1">
          <NavButton
            active={status === "active" && !folderId && !smart}
            onClick={() => onScope("active", null, null)}
            icon={<Images />}
          >
            All assets
          </NavButton>
          <NavButton
            active={status === "trash"}
            onClick={() => onScope("trash", null, null)}
            icon={<Trash2 />}
          >
            Trash
          </NavButton>
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span>Folders</span>
            {canWrite ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-[11px] font-medium normal-case tracking-normal"
                aria-label="Create root folder"
                onClick={() => openFolderEditor("create", null)}
              >
                <FolderPlus className="size-3.5" />
                New
              </Button>
            ) : null}
          </div>
          {tree.roots.length ? (
            tree.roots.map((node) => (
              <FolderBranch
                key={node.id}
                appId={appId}
                canWrite={canWrite}
                folders={folders}
                node={node}
                active={folderId}
                onSelect={(id) => onScope("active", id, null)}
                onCreate={(parentId) => openFolderEditor("create", parentId)}
                onMoveAssets={(ids, destination) =>
                  moveAssets.mutate(
                    {
                      assetIds: ids,
                      operation: { folder_id: destination },
                    },
                    { onError: (error: Error) => toast.error(error.message) }
                  )
                }
                onMoveFolder={(id, parent) =>
                  updateFolder.mutate(
                    { id, parent_id: parent },
                    { onError: (error: Error) => toast.error(error.message) }
                  )
                }
                onRename={(folder) =>
                  openFolderEditor("rename", folder.parent_id, folder)
                }
                onDelete={setPendingDelete}
              />
            ))
          ) : (
            <p className="px-2 text-xs text-muted-foreground">No folders</p>
          )}
        </div>
        <div>
          <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Smart views
          </div>
          {smartViews.map(([value, label, icon]) => (
            <NavButton
              key={value}
              active={status === "active" && smart === value}
              onClick={() => onScope("active", null, value)}
              icon={icon}
            >
              {label}
            </NavButton>
          ))}
        </div>
      </nav>
      <Dialog
        open={folderEditor !== null}
        onOpenChange={(open) => !open && setFolderEditor(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {folderEditor?.mode === "rename"
                ? "Rename folder"
                : "Create folder"}
            </DialogTitle>
            <DialogDescription>
              {folderEditor?.mode === "rename"
                ? "Choose a clear name your team will recognize."
                : folderEditor?.parentId
                ? "Add a nested folder inside the selected folder."
                : "Add a folder at the library root."}
            </DialogDescription>
          </DialogHeader>
          <label className="space-y-2 text-sm font-medium">
            Folder name
            <Input
              autoFocus
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") saveFolder();
              }}
              placeholder="e.g. Onboarding"
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderEditor(null)}>
              Cancel
            </Button>
            <Button disabled={!folderName.trim()} onClick={saveFolder}>
              {folderEditor?.mode === "rename" ? "Save name" : "Create folder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Only empty folders can be deleted. Assets and nested folders must
              be moved first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                if (pendingDelete)
                  deleteFolder.mutate(pendingDelete.id, {
                    onError: (error: Error) => toast.error(error.message),
                  });
                setPendingDelete(null);
              }}
            >
              Delete folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
function NavButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactElement;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary/70 hover:text-foreground",
        active && "bg-primary/10 font-medium text-primary"
      )}
    >
      <span className="[&>svg]:size-4">{icon}</span>
      {children}
    </button>
  );
}
function FolderBranch({
  appId,
  canWrite,
  folders,
  node,
  active,
  onSelect,
  onCreate,
  onMoveAssets,
  onMoveFolder,
  onRename,
  onDelete,
  depth = 0,
}: {
  appId: string | null;
  canWrite: boolean;
  folders: AssetFolder[];
  node: FolderNode;
  active: string | null;
  onSelect: (id: string) => void;
  onCreate: (parent: string | null) => void;
  onMoveAssets: (ids: string[], destination: string) => void;
  onMoveFolder: (id: string, parent: string) => void;
  onRename: (folder: AssetFolder) => void;
  onDelete: (folder: AssetFolder) => void;
  depth?: number;
}) {
  return (
    <div
      className="group/folder"
      onDragOver={(event) => {
        if (canWrite) event.preventDefault();
      }}
      onDrop={(event) => {
        if (!canWrite || !appId) return;
        const assets = parseAssetDrag(
          event.dataTransfer.getData(ASSET_DRAG_MIME),
          appId
        );
        if (assets) return onMoveAssets(assets.ids, node.id);
        const folder = parseFolderDrag(
          event.dataTransfer.getData(FOLDER_DRAG_MIME),
          appId
        );
        if (folder && isFolderDropAllowed(folders, folder.id, node.id))
          onMoveFolder(folder.id, node.id);
      }}
    >
      <div
        className={cn(
          "flex items-center rounded-lg transition-colors hover:bg-secondary/70",
          active === node.id && "bg-primary/10 text-primary"
        )}
        style={{ marginLeft: depth * 12 }}
      >
        <button
          type="button"
          draggable={canWrite && Boolean(appId)}
          onDragStart={(event) =>
            event.dataTransfer.setData(
              FOLDER_DRAG_MIME,
              JSON.stringify({ kind: "folder", appId, id: node.id })
            )
          }
          onClick={() => onSelect(node.id)}
          className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-2 text-left text-sm"
        >
          <Folder
            className={cn(
              "size-4 shrink-0 text-muted-foreground",
              active === node.id && "fill-primary/15 text-primary"
            )}
          />
          <span className="truncate">{node.name}</span>
        </button>
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="mr-1 size-7 text-muted-foreground/70 transition-colors hover:text-foreground"
                aria-label={`Actions for ${node.name}`}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={() => onCreate(node.id)}>
                <FolderPlus />
                New folder inside
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onRename(node)}>
                <Pencil />
                Rename folder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => onDelete(node)}
              >
                <Trash2 />
                Delete folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {node.children.map((child) => (
        <FolderBranch
          key={child.id}
          appId={appId}
          canWrite={canWrite}
          folders={folders}
          node={child}
          active={active}
          onSelect={onSelect}
          onCreate={onCreate}
          onMoveAssets={onMoveAssets}
          onMoveFolder={onMoveFolder}
          onRename={onRename}
          onDelete={onDelete}
          depth={depth + 1}
        />
      ))}
    </div>
  );
}

function AssetGrid({
  appId,
  canWrite,
  assets,
  view,
  selected,
  onSelect,
  onOpen,
  onRename,
  onDownload,
  onSelectAll,
}: {
  appId: string | null;
  canWrite: boolean;
  assets: Asset[];
  view: AssetExplorerView;
  selected: Set<string>;
  onSelect: (asset: Asset, event: React.MouseEvent) => void;
  onOpen: (asset: Asset) => void;
  onRename: (asset: Asset) => void;
  onDownload: (asset: Asset) => void;
  onSelectAll: () => void;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Click to select · Shift-click for a range
        </p>
        <Button variant="ghost" size="sm" className="h-8" onClick={onSelectAll}>
          <Check className="size-3.5" /> Select visible
        </Button>
      </div>
      <div
        className={
          view === "grid"
            ? "grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3"
            : "divide-y rounded-lg border"
        }
      >
        {assets.map((asset) => (
          <div
            key={asset.id}
            role="button"
            tabIndex={0}
            aria-label={`View ${assetName(asset)}`}
            aria-pressed={selected.has(asset.id)}
            draggable={canWrite && Boolean(appId)}
            onDragStart={(event) =>
              event.dataTransfer.setData(
                ASSET_DRAG_MIME,
                JSON.stringify({
                  kind: "asset",
                  appId,
                  ids: selected.has(asset.id) ? [...selected] : [asset.id],
                })
              )
            }
            onClick={(event) => onSelect(asset, event)}
            onDoubleClick={() => onOpen(asset)}
            onKeyDown={(event) => {
              if (event.key === "Enter") onOpen(asset);
            }}
            className={cn(
              view === "grid"
                ? "group/card relative overflow-hidden rounded-xl border border-border/70 bg-card text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                : "group/card relative flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-secondary/40",
              selected.has(asset.id) &&
                "border-primary bg-primary/[0.03] ring-2 ring-primary/20"
            )}
          >
            {view === "grid" ? (
              <span
                className={cn(
                  "absolute left-2.5 top-2.5 z-10 grid size-5 place-items-center rounded-md border bg-background/90 text-transparent shadow-sm backdrop-blur",
                  selected.has(asset.id) &&
                    "border-primary bg-primary text-primary-foreground"
                )}
              >
                <Check className="size-3.5" />
              </span>
            ) : null}
            <span
              className={
                view === "grid"
                  ? "grid aspect-[4/3] place-items-center overflow-hidden bg-[radial-gradient(circle_at_top,_hsl(var(--secondary)),_hsl(var(--muted)/0.45))] p-3"
                  : "grid size-11 shrink-0 place-items-center overflow-hidden rounded border bg-muted/40"
              }
            >
              <AssetPreview
                asset={asset}
                decorative
                className={cn(
                  "size-full",
                  view === "grid"
                    ? "rounded-md object-contain drop-shadow-sm"
                    : "object-cover"
                )}
              />
            </span>
            <span
              className={
                view === "grid"
                  ? "block min-w-0 border-t px-3 py-2.5"
                  : "min-w-0 flex-1"
              }
            >
              <span
                className="block truncate text-sm font-semibold tracking-tight"
                title={assetName(asset)}
              >
                {assetName(asset)}
              </span>
              <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                {asset.content_type.startsWith("image/")
                  ? assetDetails(asset)
                  : asset.content_type.replace("font/", "").toUpperCase() +
                    " font"}
                {asset.byte_size !== null
                  ? ` · ${Math.round(asset.byte_size / 1024)} KB`
                  : ""}
              </span>
            </span>
            <span
              onClick={(event) => event.stopPropagation()}
              className={view === "grid" ? "absolute right-2 top-2" : "pr-1"}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className={cn(
                      "size-8 shadow-sm",
                      view === "grid" &&
                        "bg-background/90 text-muted-foreground backdrop-blur transition-colors hover:text-foreground"
                    )}
                    aria-label={`Actions for ${assetName(asset)}`}
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onOpen(asset)}>
                    <FileType2 />
                    View details
                  </DropdownMenuItem>
                  {canWrite ? (
                    <DropdownMenuItem onSelect={() => onRename(asset)}>
                      <Pencil />
                      Rename file
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem onSelect={() => onDownload(asset)}>
                    <Download />
                    Download
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BulkToolbar({
  count,
  folders,
  status,
  pending,
  onRun,
  onClear,
}: {
  count: number;
  folders: AssetFolder[];
  status: AssetStatus;
  pending: boolean;
  onRun: (operation: AssetBulkOperation) => void;
  onClear: () => void;
}) {
  const [tag, setTag] = useState("");
  const [moveTo, setMoveTo] = useState("__move");
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-primary/15 bg-primary/[0.055] px-3 py-2.5 text-sm">
      <span className="mr-1 flex items-center gap-2 font-semibold text-primary">
        <span className="grid size-6 place-items-center rounded-md bg-primary text-xs text-primary-foreground">
          {count}
        </span>
        selected
      </span>
      <Input
        className="h-8 w-40 bg-background"
        placeholder="Enter a tag"
        value={tag}
        onChange={(e) => setTag(e.target.value)}
      />
      <Button
        size="sm"
        variant="outline"
        disabled={!tag.trim() || pending}
        onClick={() => onRun({ add_tags: [tag] })}
      >
        <Tag className="size-3.5" /> Add tag
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!tag.trim() || pending}
        onClick={() => onRun({ remove_tags: [tag] })}
      >
        <X className="size-3.5" /> Remove tag
      </Button>
      <Select
        value={moveTo}
        onValueChange={(value) => {
          setMoveTo("__move");
          if (value !== "__move")
            onRun({ folder_id: value === "root" ? null : value });
        }}
      >
        <SelectTrigger
          aria-label="Move selected"
          className="h-8 w-40 bg-background"
        >
          <FolderInput className="size-3.5 text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__move">Move to…</SelectItem>
          <SelectItem value="root">Library root</SelectItem>
          {folders.map((folder) => (
            <SelectItem key={folder.id} value={folder.id}>
              {folder.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        variant={status === "trash" ? "outline" : "destructive"}
        disabled={pending}
        onClick={() =>
          onRun({ lifecycle: status === "trash" ? "restore" : "trash" })
        }
      >
        {status === "trash" ? <ArchiveRestore /> : <Trash2 />}
        {status === "trash" ? "Restore selected" : "Move to Trash"}
      </Button>
      <Button
        size="icon"
        variant="ghost"
        aria-label="Clear selection"
        onClick={onClear}
      >
        <X />
      </Button>
    </div>
  );
}

function UploadQueue({
  items,
  onRetry,
  onDismiss,
}: {
  items: QueueItem[];
  onRetry: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  if (!items.length) return null;
  return (
    <section
      aria-label="Upload queue"
      className="fixed bottom-4 right-4 z-40 max-h-72 w-80 overflow-auto rounded-lg border bg-background p-3 shadow-xl"
    >
      <div className="mb-2 font-medium">Uploads</div>
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-2 border-t py-2 text-xs"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{item.file.name}</span>
            <span
              className={
                item.state === "error"
                  ? "text-destructive"
                  : "text-muted-foreground"
              }
            >
              {item.error ?? item.state}
            </span>
          </span>
          {item.state === "error" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRetry(item.id)}
            >
              Retry
            </Button>
          ) : null}
          {item.state === "success" || item.state === "error" ? (
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Dismiss ${item.file.name}`}
              onClick={() => onDismiss(item.id)}
            >
              <X />
            </Button>
          ) : null}
        </div>
      ))}
    </section>
  );
}

function RenameAssetDialog({
  asset,
  pending,
  onClose,
  onSave,
}: {
  asset: Asset | null;
  pending: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(asset ? assetName(asset) : "");
  return (
    <Dialog open={asset !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename file</DialogTitle>
          <DialogDescription>
            This changes the library name only. Immutable revision URLs stay
            unchanged.
          </DialogDescription>
        </DialogHeader>
        <label className="space-y-2 text-sm font-medium">
          File name
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && name.trim()) onSave(name.trim());
            }}
          />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || pending}
            onClick={() => onSave(name.trim())}
          >
            <Pencil className="size-4" />
            Save name
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetDetail({
  asset,
  folders,
  canWrite,
  onClose,
}: {
  asset: Asset | null;
  folders: AssetFolder[];
  canWrite: boolean;
  onClose: () => void;
}) {
  const update = useUpdateAsset(asset?.app_id ?? null);
  const replace = useReplaceAsset(asset?.app_id ?? null);
  const trash = useTrashAsset(asset?.app_id ?? null);
  const restore = useRestoreAsset(asset?.app_id ?? null);
  const purge = usePurgeAsset(asset?.app_id ?? null);
  const revisions = useAssetRevisions(asset?.id ?? null);
  const [tab, setTab] = useState("details");
  const usage = useAssetUsage(asset?.id ?? null, tab === "usage");
  const replaceInput = useRef<HTMLInputElement>(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [form, setForm] = useState(() => ({
    display_name: asset?.display_name ?? (asset ? assetName(asset) : ""),
    title: asset?.title ?? "",
    caption: asset?.caption ?? "",
    alt_text: asset?.alt_text ?? "",
    tags: asset?.tags.join(", ") ?? "",
    folder_id: asset?.folder_id ?? "",
  }));
  if (!asset) return <></>;
  const isImage = asset.content_type.startsWith("image/");
  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{assetName(asset)}</DialogTitle>
            <DialogDescription>
              {assetDetails(asset)} · revision{" "}
              {asset.revision_number ?? "unknown"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-[220px_1fr]">
            <div>
              <div className="grid h-52 place-items-center overflow-hidden rounded-lg border bg-muted/40">
                <AssetPreview asset={asset} />
              </div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 text-xs">
                <dt className="text-muted-foreground">Uploaded</dt>
                <dd>{new Date(asset.created_at).toLocaleString()}</dd>
                <dt className="text-muted-foreground">Size</dt>
                <dd>
                  {asset.byte_size === null
                    ? "Unknown"
                    : `${Math.round(asset.byte_size / 1024)} KB`}
                </dd>
                {asset.width && asset.height ? (
                  <>
                    <dt className="text-muted-foreground">Dimensions</dt>
                    <dd>
                      {asset.width} × {asset.height}
                    </dd>
                  </>
                ) : null}
              </dl>
              <Button
                className="mt-3 w-full"
                variant="outline"
                onClick={() =>
                  void downloadAsset(asset).catch((e: Error) =>
                    toast.error(e.message)
                  )
                }
              >
                <Download />
                Download
              </Button>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="revisions">Revisions</TabsTrigger>
                <TabsTrigger value="usage">Usage</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="space-y-3">
                <Field
                  label="File name"
                  value={form.display_name}
                  onChange={(value) =>
                    setForm({ ...form, display_name: value })
                  }
                />
                <Field
                  label="Title"
                  value={form.title}
                  onChange={(value) => setForm({ ...form, title: value })}
                />
                {isImage ? (
                  <>
                    <Field
                      label="Caption"
                      value={form.caption}
                      onChange={(value) => setForm({ ...form, caption: value })}
                    />
                    <Field
                      label="Accessibility default"
                      value={form.alt_text}
                      onChange={(value) =>
                        setForm({ ...form, alt_text: value })
                      }
                    />
                  </>
                ) : null}
                <Field
                  label="Tags (comma separated)"
                  value={form.tags}
                  onChange={(value) => setForm({ ...form, tags: value })}
                />
                <label className="block space-y-1.5 text-xs font-medium">
                  Folder
                  <Select
                    value={form.folder_id || "__root"}
                    onValueChange={(value) =>
                      setForm({
                        ...form,
                        folder_id: value === "__root" ? "" : value,
                      })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <Folder className="size-4 text-muted-foreground" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__root">Library root</SelectItem>
                      {folders.map((folder) => (
                        <SelectItem key={folder.id} value={folder.id}>
                          {folder.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                {canWrite ? (
                  <Button
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate(
                        {
                          id: asset.id,
                          display_name: form.display_name,
                          title: form.title || null,
                          caption: isImage ? form.caption || null : null,
                          alt_text: isImage ? form.alt_text || null : null,
                          tags: form.tags
                            .split(",")
                            .map((value) => value.trim())
                            .filter(Boolean),
                          folder_id: form.folder_id || null,
                        },
                        {
                          onSuccess: () => toast.success("Asset details saved"),
                          onError: (e: Error) => toast.error(e.message),
                        }
                      )
                    }
                  >
                    <Check className="size-4" /> Save changes
                  </Button>
                ) : null}
              </TabsContent>
              <TabsContent value="revisions">
                {revisions.isLoading ? (
                  <Skeleton className="h-24" />
                ) : (
                  <div className="divide-y">
                    {revisions.data?.map((revision) => (
                      <div
                        key={revision.id}
                        className="flex items-center gap-2 py-2 text-xs"
                      >
                        <Badge
                          variant={
                            revision.id === asset.current_revision_id
                              ? "default"
                              : "outline"
                          }
                        >
                          v{revision.revision_number}
                        </Badge>
                        <span className="flex-1">
                          {new Date(revision.created_at).toLocaleString()} ·{" "}
                          {revision.byte_size === null
                            ? "unknown size"
                            : `${Math.round(
                                revision.byte_size / 1024
                              )} KB`}{" "}
                          · {revision.content_type}
                        </span>
                        <a href={revision.url} target="_blank" rel="noreferrer">
                          Preview
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="usage">
                {usage.isLoading ? (
                  <Skeleton className="h-24" />
                ) : usage.isError ? (
                  <p className="text-sm text-destructive">
                    Usage could not be proven complete:{" "}
                    {usage.error instanceof Error
                      ? usage.error.message
                      : "Try again."}
                  </p>
                ) : usage.data?.length ? (
                  <div className="space-y-3">
                    <UsageGroup
                      title="Mutable drafts"
                      rows={usage.data.filter(
                        (row) => row.reference_kind === "draft"
                      )}
                    />
                    <UsageGroup
                      title="Retained bundles"
                      rows={usage.data.filter(
                        (row) => row.reference_kind === "bundle"
                      )}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No draft or retained bundle references.
                  </p>
                )}
              </TabsContent>
            </Tabs>
          </div>
          {canWrite ? (
            <DialogFooter className="flex-wrap">
              <input
                ref={replaceInput}
                type="file"
                accept={
                  isImage ? ".png,.jpg,.jpeg,.webp,.gif" : ".woff2,.ttf,.otf"
                }
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file)
                    replace.mutate(
                      { id: asset.id, file },
                      {
                        onSuccess: () =>
                          toast.success("New immutable revision created"),
                        onError: (error: Error) => toast.error(error.message),
                      }
                    );
                  e.target.value = "";
                }}
              />
              {asset.deleted_at ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() =>
                      restore.mutate(asset.id, { onSuccess: onClose })
                    }
                  >
                    <ArchiveRestore />
                    Restore
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={
                      usage.isLoading ||
                      usage.isError ||
                      !usage.data ||
                      usage.data.length > 0
                    }
                    onClick={() => setConfirmPurge(true)}
                  >
                    Permanently purge
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => replaceInput.current?.click()}
                  >
                    Replace file
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() =>
                      trash.mutate(asset.id, { onSuccess: onClose })
                    }
                  >
                    <Trash2 />
                    Move to Trash
                  </Button>
                </>
              )}
            </DialogFooter>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Replacement creates a new immutable revision. Managed drafts move to
            it; retained bundles keep their original bytes. Trash may make
            managed drafts fail preview or publish, but already-published
            bundles remain intact.
          </p>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently purge this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              Every immutable revision will be deleted. This cannot be undone
              and is refused if any usage remains.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white"
              onClick={() =>
                purge.mutate(asset.id, {
                  onSuccess: onClose,
                  onError: (e: Error) => toast.error(e.message),
                })
              }
            >
              Permanently purge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-xs font-medium">
      {label}
      <Input
        className="mt-1"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function UsageGroup({
  title,
  rows,
}: {
  title: string;
  rows: import("@/lib/queries").AssetUsage[];
}) {
  if (!rows.length) return null;
  return (
    <section>
      <h3 className="text-sm font-medium">{title}</h3>
      {rows.map((row, index) => (
        <div
          key={`${row.revision_id}-${row.path}-${index}`}
          className="mt-1 rounded border p-2 text-xs"
        >
          <div className="font-medium">
            {row.experience_name ?? row.environment_name ?? "Unknown reference"}
            {row.bundle_version !== null
              ? ` · bundle v${row.bundle_version}`
              : ""}
          </div>
          <div className="text-muted-foreground">
            revision {row.revision_number} · {row.path}
          </div>
          {row.experience_id ? (
            <Link
              to="/experiences/$id/edit"
              params={{ id: row.experience_id }}
              className="text-primary"
            >
              Open experience
            </Link>
          ) : null}
        </div>
      ))}
    </section>
  );
}
